<script setup lang="ts">
import { useI18n } from 'vue-i18n'
import { inject, Ref } from 'vue'
import { UsePolozkaStoreType } from '/@src/stores/rozpocet'
import { formatZdrojFinancovaniaZhrnutie } from '/@src/utils/format'
import { formatMoney } from '/@src/utils/moneyFormatter'
import { usePrilohaDownload } from './usePrilohaDownload'

export interface RozpocetVydavokProps {
  store: UsePolozkaStoreType
}

const { t } = useI18n()
const kodIrz = inject<Ref<string>>('kodIrz')
const stavPodana = inject<boolean>('stavPodana', false)
const bearerTokenIS = inject<string | undefined>('bearerTokenIS')
const ziadostId = inject<Ref<string | undefined>>('ziadostId')

const props = defineProps<RozpocetVydavokProps>()

const getDokument = usePrilohaDownload({
  stavPodana,
  ziadostId: ziadostId?.value,
  bearerTokenIS,
  kodIrz: kodIrz?.value,
})
</script>

<template>
  <template v-if="store?.sumaZaVsetkyVydavky">
    <DisplayList variant="inline" inline-layout="one-half" class="mb-0">
      <dt>
        <IDSKHeading as="h3" variant="m" class="mb-2">
          <slot name="title"></slot>
        </IDSKHeading>
      </dt>
      <dd class="govuk-!-font-weight-bold mb-2">
        <slot name="sum">
          {{ formatMoney(store?.sumaZaVsetkyVydavky) }}
        </slot>
      </dd>
    </DisplayList>
    <DisplayList
      v-for="value in props.store?.entries"
      :key="value"
      variant="inline"
      inline-layout="one-half"
      class="mb-5"
    >
      <template #default>
        <dt class="mb-2">{{ value?.nazov }}</dt>
        <dd class="mb-2">
          {{ formatMoney(props.store?.sumaZaVydavok(value?.id)) }}
        </dd>
        <template v-for="(zdroj, key) in value?.zdrojeFinancovania" :key="key">
          <template v-if="zdroj?.suma?.sumaEurBezDph">
            <dt class="ml-4 mb-2">
              {{ formatZdrojFinancovaniaZhrnutie(key) }}
            </dt>
            <dd class="mb-2">
              {{ formatMoney(zdroj?.suma?.sumaEurBezDph) }}
            </dd>
          </template>
          <template v-if="zdroj?.suma?.dphEur">
            <dt class="ml-4 mb-2">
              {{ formatZdrojFinancovaniaZhrnutie(key + '_DPH') }}
            </dt>
            <dd class="mb-2">
              {{ formatMoney(zdroj?.suma?.dphEur) }}
            </dd>
          </template>
          <template v-if="zdroj?.suma?.sumaDphEur">
            <dt class="ml-4 mb-2">{{ formatZdrojFinancovaniaZhrnutie(key) }}</dt>
            <dd class="mb-2">
              {{ formatMoney(zdroj?.suma?.sumaDphEur) }}
            </dd>
          </template>
        </template>
        <template v-if="Object.keys(value?.prilohy)?.length">
          <dt class="mb-2 grid-col-span">{{ t('globalne.prilohy') }}</dt>

          <template v-for="priloha in value?.prilohy" :key="priloha">
            <template v-for="dokument in priloha?.dokumenty" :key="dokument">
              <dt class="ml-4 mb-1 grid-col-span">
                <IDSKLink
                  href="#"
                  no-visited-state
                  class="mb-0"
                  @click.prevent="
                    getDokument(
                      dokument?.dokumentRef?.identifikatorUlozisko,
                      dokument?.dokumentRef?.verziaUlozisko
                    )
                  "
                >
                  {{ dokument?.nazov + '.' + dokument?.format }}
                </IDSKLink>
              </dt>
            </template>
          </template>
        </template>
      </template>
    </DisplayList>
  </template>
</template>

<style lang="scss" scoped>
.grid-col-span {
  grid-column: 1 / span 2;
}
</style>
